import { 
  Component, 
  EventEmitter, 
  Output, 
  Input, 
  OnChanges, 
  OnInit, 
  HostBinding, 
  ElementRef, 
  HostListener, 
  SimpleChanges 
} from '@angular/core'; 
import { MatDialog } from '@angular/material/dialog'; 
import { DataEntity } from 'src/app/models/data-entities'; 
import { WorkflowService } from 'src/app/services'; 
 
@Component({ 
  selector: 'wm-column-settings', 
  templateUrl: './column-settings.component.html', 
  styleUrls: ['./column-settings.component.scss'], 
  standalone: false, 
}) 
export class ColumnSettingsComponent implements OnInit, OnChanges { 
 
  @Input() workflowId: string; 
  @Input() field: any | null = null; 
  @Input() overlayTopOffsetPx = 0; 
  @Input() containedOverlay = false; 
 
  @HostBinding('style.--column-settings-top-offset.px') 
  get hostTopOffset(): number { 
    return this.overlayTopOffsetPx; 
  } 
 
  @Output() saveColumn = new EventEmitter<any>(); 
  @Output() closePanel = new EventEmitter<void>(); 
 
column = { 
  name: '', 
  header: '', 
  dataType: 'text', 
  isAvailableColumn: true 
}; 
 
  entities: DataEntity[] = []; 
  entityOptions: { label: string; value: string }[] = []; 
  filteredEntityOptions: { label: string; value: string }[] = []; 
  nameSearch = ''; 
  showNameDropdown = false; 
  showDataTypeDropdown = false; 
 
  constructor( 
    private workflowSvc: WorkflowService, 
    private dialog: MatDialog, 
    private elRef: ElementRef<HTMLElement> 
  ) { } 
 
  ngOnInit() { 
    this.loadEntities(); 
    this.syncColumnFromInput(); 
  } 
 
  ngOnChanges(changes: SimpleChanges) { 
    if (changes['field']) { 
      this.syncColumnFromInput(); 
    } 
  } 
 
  loadEntities() { 
    this.workflowSvc 
      .getWorkflowVersionDataEntities(null) 
      .subscribe((res: DataEntity[]) => { 
 
        this.entities = res || []; 
 
        this.entityOptions = this.entities.map(e => ({ 
          label: e.label || e.templateCode, 
          value: e.templateCode 
        })); 
 
        this.filteredEntityOptions = [...this.entityOptions]; 
 
      }); 
  } 

  dataTypeOptions = [ 
    { label: 'Text', value: 'text' }, 
    { label: 'Date', value: 'date' },
    { label: 'Number with Decimals', value: 'decimal' }, 
    { label: 'Number without Decimals', value: 'integer' } 
    
  ]; 
 
onNameChange(selectedValue: string) { 
  this.column.name = selectedValue; 
  this.nameSearch = 
    this.entityOptions.find(option => option.value === selectedValue)?.label || 
    selectedValue; 
  this.showNameDropdown = false; 
} 
 
mapDataType(type: string): number { 
 
  switch (type) { 
    case 'text': 
      return 1; 
    case 'date': 
      return 2; 
    case 'decimal': 
      return 3; 
    case 'integer': 
      return 4; 
    default: 
      return 0; 
  } 
 
} 
 
openNameDropdown() { 
  this.showNameDropdown = true; 
  this.showDataTypeDropdown = false; 
  // Added by Amisha on 05/21/2026 to keep the dropdown results aligned with 
  // whatever the user has already typed before explicitly opening search. 
  this.filterNameOptions(this.nameSearch); 
} 
 
onNameSearchChange(value: string) { 
  this.nameSearch = value; 
  // Added by Amisha on 05/21/2026 so users can save free-typed column names 
  // without being forced to pick an entity from the Global Entities list. 
  this.column.name = value; 
  this.showNameDropdown = false; 
  this.filterNameOptions(value); 
} 
 
selectNameOption(option: { label: string; value: string }) { 
  this.onNameChange(option.value); 
} 
 
openDataTypeDropdown() { 
  this.showDataTypeDropdown = true; 
  this.showNameDropdown = false; 
} 
 
selectDataType(option: { label: string; value: string }) { 
  this.column.dataType = option.value; 
  this.showDataTypeDropdown = false; 
} 
 
get selectedDataTypeLabel(): string { 
  return ( 
    this.dataTypeOptions.find(option => option.value === this.column.dataType) 
      ?.label || '' 
  ); 
} 
 
private filterNameOptions(searchValue: string) { 
  const normalizedValue = (searchValue || '').trim().toLowerCase(); 
 
  this.filteredEntityOptions = this.entityOptions.filter(option => 
    option.label.toLowerCase().includes(normalizedValue) || 
    option.value.toLowerCase().includes(normalizedValue) 
  ); 
} 
 
@HostListener('document:mousedown', ['$event']) 
handleDocumentClick(event: MouseEvent) { 
  const target = event.target as HTMLElement | null; 
 
  if (!target) { 
    return; 
  } 
 
  const clickedInsideComponent = this.elRef.nativeElement.contains(target); 
 
  if (!clickedInsideComponent) { 
    this.showNameDropdown = false; 
    this.showDataTypeDropdown = false; 
    return; 
  } 
 
  const clickedInsideNameDropdown = !!target.closest('.entity-name-field'); 
  const clickedInsideDataTypeDropdown = !!target.closest('.data-type-dropdown-field'); 
 
  if (!clickedInsideNameDropdown) { 
    this.showNameDropdown = false; 
  } 
 
  if (!clickedInsideDataTypeDropdown) { 
    this.showDataTypeDropdown = false; 
  } 
} 

save() { 
  if (this.isSaveDisabled) { 
    return; 
  } 
 
  const field = { 
    id: this.field?.id || crypto.randomUUID(), 
    name: this.column.name, 
    resultHeader: this.column.header, 
    dataType: this.mapDataType(this.column.dataType), 
    // Added by Amisha on 05/21/2026 so Janus column settings can persist the 
    // same column availability flag exposed by the legacy dataset designer. 
    isAvailableColumn: this.column.isAvailableColumn, 
    mappings: this.field?.mappings || [], 
    outputFormula: this.field?.outputFormula ?? null, 
    conditionalOutput: this.field?.conditionalOutput ?? false, 
    isStatic: this.field?.isStatic 
  }; 
 
  this.saveColumn.emit(field); 
 
  this.close(); 
} 
 
  get isSaveDisabled(): boolean { 
    return !this.column.name?.trim() || !this.column.header?.trim() || !this.column.dataType?.trim(); 
  } 
 
  close() { 
    this.closePanel.emit(); 
  } 
 
  get headerTitle(): string { 
    return this.field ? 'Edit Column Settings' : 'New Column Settings'; 
  } 
 
  private syncColumnFromInput() { 
    const inputField = this.field || {}; 
    const dataType = this.mapFieldDataType(inputField?.dataType); 
 
    this.column = { 
      name: inputField?.name || '', 
      header: inputField?.resultHeader || inputField?.header || '', 
      dataType: inputField?.dataType != null ? dataType : 'text', 
      // Added by Amisha on 05/21/2026 to default Janus columns to available, 
      // matching the legacy dataset designer when the flag is missing. 
      isAvailableColumn: 
        inputField?.isAvailableColumn === undefined || 
        inputField?.isAvailableColumn === null 
          ? true 
          : !!inputField.isAvailableColumn 
    }; 
    this.nameSearch = this.resolveNameSearch(this.column.name); 
    this.filteredEntityOptions = [...this.entityOptions]; 
  } 
 
  private resolveNameSearch(value: string): string { 
    return ( 
      this.entityOptions.find(option => option.value === value)?.label || 
      value || 
      '' 
    ); 
  } 
 
  private mapFieldDataType(dataType: number | string | null | undefined): string { 
    if (dataType === 1 || dataType === 'string') { 
      return 'text'; 
    } 
 
    if (dataType === 2 || dataType === 'date') { 
      return 'date'; 
    }

    if (dataType === 3 || dataType === 'decimal') { 
      return 'decimal'; 
    } 
 
    if (dataType === 4 || dataType === 'integer') { 
      return 'integer'; 
    } 
 
    return 'text'; 
  } 
}